import { toPath } from '@shared/util/object';
import Yup from '../yupWithCustomValidations';
import { FormErrors, InitialValues, Value } from './store';

export const updateIn = <T = InitialValues>(
  obj: T,
  path: string | ReturnType<typeof toPath>,
  value: Value | null,
): T => {
  const updated = { ...obj };

  /* eslint-disable no-param-reassign */
  const walk = (
    obj: any,
    path: ReturnType<typeof toPath>,
    value: Parameters<typeof updateIn>[2],
  ) => {
    const key = path.shift()!;
    obj[key] = Array.isArray(obj[key]) ? [...obj[key]] : { ...obj[key] };

    // We assume that when a "key" is a number we want an array.
    // When navigating an empty object, we make sure
    // we create an array and not an object.
    if (typeof path[0] === 'number' && Object.keys(obj[key]).length === 0) {
      obj[key] = [];
    }

    if (path.length === 0) {
      if (value === undefined) {
        delete obj[key];
      } else {
        obj[key] = value;
      }
      return;
    }

    walk(obj[key], path, value);
  };
  /* eslint-enable no-param-reassign */

  walk(updated, typeof path === 'string' ? toPath(path) : path, value);

  return updated;
};

export const cleanUpErrors = <T>(errors: FormErrors<T>) => {
  /* eslint-disable no-param-reassign */
  Object.entries(errors).forEach(([key, value]) => {
    if (value && typeof value === 'object') {
      cleanUpErrors(value);
    }

    // eslint-disable-next-line eqeqeq
    if ((value && typeof value === 'object' && !Object.keys(value).length) || value == null) {
      if (Array.isArray(value)) {
        value.splice(key as unknown as number, 1);

        if (!value.filter(Boolean).length) {
          delete errors[key as keyof T];
        }
      } else {
        delete errors[key as keyof T];
      }
    }
  });
  /* eslint-enable no-param-reassign */

  return errors;
};

export const cleanUpNullValues = <T>(errors: FormErrors<T>) => {
  const result = {} as Record<string, any>; // Not the best typing.

  Object.entries(errors).forEach(([field, value]) => {
    if (Array.isArray(value)) {
      const hasErros = value.some((v) => !!v);

      if (hasErros) {
        result[field] = value as Value[];
      }
    } else if (value) {
      result[field] = value as Value;
    }
  });

  return result;
};

export const parseYupErrors = (error: Yup.ValidationError) => {
  const errors: Record<string, string> = {};
  error.inner.forEach(({ path, message }) => (errors[path!] = message));
  return errors;
};
